import Joi from 'joi';
import { useEffect, useState } from 'react';
import { IoArrowBackSharp } from 'react-icons/io5';
import { FormattedMessage, useIntl } from 'react-intl';
import { useNavigate } from 'react-router-dom';
import 'react-toastify/dist/ReactToastify.css';
import { Button, Col, Label, Row, Spinner } from 'reactstrap';
import ErrorMessageComponent from '../../components/ErrorMessageComponent';
import InputComponent from '../../components/InputComponent';
import PasswordCheckList from '../../components/PasswordCheckList';
import { useAuth } from '../../context/AuthContext';
import { useAvailableModulesContext } from '../../context/AvailableModulesContext';
import Toast from '../../helpers/Toast';
import { Utilities } from '../../helpers/Utilities';
import { ResetPasswordDTO } from '../../models/ResetPasswordDTO';
import { UserService } from '../../services/UserService';
import Banner from './Banner';

export default function ResetPassword() {
    const userService = new UserService();
    const { reactAppName } = useAvailableModulesContext();
    const intl = useIntl();
    const navigate = useNavigate();
    const { isUserLoggedIn } = useAuth();
    const [userId] = useState<string>(Utilities.LoadParameterFromURLQuery("userId", "string", null));
    const [token] = useState<string>(Utilities.LoadParameterFromURLQuery("token", "string", null));
    const [password, setPassword] = useState<string>("");
    const [password2, setPassword2] = useState<string>("");
    const [validationResult, setValidationResult] = useState<any[]>([]);
    const [loading, setLoading] = useState(false);
    const [isPasswordValid, setIsPasswordValid] = useState(false);
    const [isConfirmPasswordValid, setIsConfirmPasswordValid] = useState(false);

    const schema = Joi.object({
        password: Joi.string().pattern(Utilities.PasswordRegularExpression).min(8).required().messages({
            'any.required': intl.formatMessage({ id: "Screens.Auth.Validation.PasswordRequired" }),
            'string.base': intl.formatMessage({ id: "Screens.Auth.Validation.PasswordRequired" }),
            'string.empty': intl.formatMessage({ id: "Screens.Auth.Validation.PasswordRequired" }),
            'string.min': intl.formatMessage({ id: "Screens.Auth.Validation.PasswordMin" }),
            'string.pattern.base': intl.formatMessage({ id: "Screens.Auth.Validation.PasswordFormat" }),
        }),
        password2: Joi.string().valid(Joi.ref('password')).required().messages({
            'any.required': intl.formatMessage({ id: "Screens.Auth.Validation.PasswordRequired" }),
            'string.base': intl.formatMessage({ id: "Screens.Auth.Validation.PasswordRequired" }),
            'string.empty': intl.formatMessage({ id: "Screens.Auth.Validation.PasswordRequired" }),
            'any.only': intl.formatMessage({ id: "Screens.Auth.Validation.PasswordSame" }),
        })
    });

    const resetPassword = () => {
        setLoading(true);

        validateForm(password, password2).then(async (result: any) => {
            if (result.error != null) {
                setValidationResult(result.error.details);
            } else {
                setValidationResult([]);

                const data: ResetPasswordDTO = {
                    userID: userId,
                    password: password,
                    token: token
                }

                var response = await userService.ResetPassword(data);

                if (response.success === true) {

                    Toast.Show("success", response.message);

                    setLoading(false);

                    //Redirecionamos o user   
                    navigate("/login");
                }
                else {
                    Toast.Show("error", response.message);
                }
            }
            setLoading(false);

        }).catch(() => {
            Toast.Show("error", "Ocorreu um erro ao redefinir a password. Por favor tente novamente dentro de segundos.");
        })
        setLoading(false);

    }

    const validateForm = (password: String, password2: String) => {
        return new Promise(function (resolve) {
            var validationResult = schema.validate(
                {
                    password: password,
                    password2: password2,
                }, { abortEarly: false });
            resolve(validationResult);
        });
    }

    useEffect(() => {
        if (isUserLoggedIn) {
            navigate('/');
        }
    }, [isUserLoggedIn]);

    return (
        <Row style={{ display: "flex", height: "100%", margin: "0px", padding: "0px" }}>

            {/* Image on left */}
            <Banner />

            {/* Right*/}
            <Col md={6} style={{ display: "flex", alignItems: "center", alignContent: "center", justifyContent: "center", flexDirection: "column" }}>

                <Row style={{ marginBottom: "20px", width: "50%" }}>
                    <Col md={12} className="siteNameContainer">
                        <span className="siteNameBackLogo"><IoArrowBackSharp className="pageTitleBackIcon" onClick={() => { navigate('/login'); }} /></span>
                        <Label className='siteName'>{reactAppName}</Label>
                        <span style={{ flex: 1 }}></span>
                    </Col>
                </Row>

                <Row style={{ marginBottom: "20px" }}>
                    <Col md={12} className="siteWelcome">
                        <Label><FormattedMessage id="Screens.Auth.SubTitle.ResetPassword" /></Label>
                    </Col>
                </Row>

                {/* Input password*/}
                <Row style={{ margin: "0px 0px 20px 0px", padding: "0px", minWidth: "50%" }}>
                    <InputComponent
                        xl={12}
                        lg={12}
                        md={12}
                        sm={12}
                        xs={12}
                        style={{ textAlign: "center", width: "100%" }}
                        type={"password"}
                        value={password}
                        label={intl.formatMessage({ id: "Screens.Auth.Input.Password" })}
                        readOnly={false}
                        onChange={(text: string) => setPassword(text)}
                    />

                </Row>

                <PasswordCheckList password={password}
                    shouldHaveAtLeastOneNumber={true}
                    shouldHaveAtLeastOneLowercaseLetter={true}
                    shouldHaveAtLeastOneLetter={true}
                    shouldHaveAtLeastOneSpecialChar={true}
                    shouldHaveAtLeastOneCapitalLetter={true}
                    minimumCharacters={8}
                    matchPasswords={false}
                    onChange={setIsPasswordValid}
                />
                <ErrorMessageComponent validationResult={validationResult} fieldToValidate="password" />

                {/* Input password2*/}
                <Row style={{ margin: "0px 0px 10px 0px", padding: "0px", minWidth: "50%" }}>
                    <InputComponent
                        xl={12}
                        lg={12}
                        md={12}
                        sm={12}
                        xs={12}
                        style={{ textAlign: "center", width: "100%" }}
                        type={"password"}
                        value={password2}
                        label={intl.formatMessage({ id: "Screens.Auth.Input.RepeatPassword" })}
                        readOnly={false}
                        onChange={(text: string) => setPassword2(text)}
                    />
                </Row>

                <PasswordCheckList password={password}
                    confirmPassword={password2}
                    matchPasswords={true}
                    onChange={setIsConfirmPasswordValid}
                />
                <ErrorMessageComponent validationResult={validationResult} fieldToValidate="password2" />

                <Row style={{ margin: "20px 0px 50px 0px", padding: "0px", minWidth: "50%" }}>
                    <Col md={12} sm={12} xs={12} lg={12} style={{ textAlign: "center" }}>
                        <Row className="loginbuttons" style={{ margin: "0px", padding: "0px", minWidth: "50%" }}>
                            {
                                loading === true ?
                                    <Col md={12} sm={12} xs={12} lg={12} style={{ textAlign: "center" }}>
                                        <Spinner color="primary" />
                                    </Col>
                                    :
                                    <Col md={12} sm={12} xs={12} lg={12} style={{ textAlign: "center" }}>
                                        <Button disabled={isPasswordValid === false || isConfirmPasswordValid === false} className="resetPassword" onClick={resetPassword}><FormattedMessage id="Screens.Auth.Button.ResetPassword" /></Button>
                                    </Col>
                            }
                        </Row>
                    </Col>
                </Row>
            </Col>
        </Row>
    )
}